import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Eye, Loader2, Pencil, Share2, Trash2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useNotification } from '../context/NotificationContext';
import { apiService } from '../services/apiService';
import ShareRunModal from '../components/ShareRunModal';

type LifecycleTab = 'owned' | 'shared';

const TdmTaskLifecyclePage: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useApp();
  const { showError, showSuccess } = useNotification();
  const [activeTab, setActiveTab] = useState<LifecycleTab>('owned');
  const [isDownloading, setIsDownloading] = useState(false);
  const [shareRunId, setShareRunId] = useState<string | null>(null);

  const loadRuns = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const runs = await apiService.getRuns();
      dispatch({ type: 'SET_RUNS', payload: Array.isArray(runs) ? runs : [] });
    } catch (error) {
      showError('Error', error instanceof Error ? error.message : 'Failed to load runs');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  useEffect(() => {
    loadRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRuns = useMemo(() => {
    if (activeTab === 'owned') return state.runs.filter((r: any) => !!r.owned);
    return state.runs.filter((r: any) => !!r.shared_with_me);
  }, [activeTab, state.runs]);

  const formatDate = (timestamp?: string) => {
    if (!timestamp) return '-';
    try {
      const year = timestamp.substring(0, 4);
      const month = timestamp.substring(4, 6);
      const day = timestamp.substring(6, 8);
      const hour = timestamp.substring(9, 11);
      const minute = timestamp.substring(11, 13);
      return `${year}-${month}-${day} ${hour}:${minute}`;
    } catch {
      return timestamp;
    }
  };

  const formatIsoDateTime = (iso?: string) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleString(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  const handleDownloadAllCSV = async (runId: string) => {
    setIsDownloading(true);
    try {
      await apiService.downloadAllCSV(runId);
      showSuccess('Success', 'CSV files downloaded');
    } catch (error) {
      showError('Error', error instanceof Error ? error.message : 'Failed to download CSV files');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadAllXML = async (runId: string) => {
    setIsDownloading(true);
    try {
      await apiService.downloadAllXML(runId);
      showSuccess('Success', 'XML files downloaded');
    } catch (error) {
      showError('Error', error instanceof Error ? error.message : 'Failed to download XML files');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleRenameRun = async (run: any) => {
    const nextName = window.prompt('Enter new task name', run.name || '');
    if (nextName === null) return;
    const trimmed = nextName.trim();
    if (!trimmed) {
      showError('Error', 'Task name cannot be empty');
      return;
    }
    try {
      await apiService.renameRun(run.id, trimmed);
      showSuccess('Success', 'Task renamed successfully');
      await loadRuns();
    } catch (error) {
      showError('Error', error instanceof Error ? error.message : 'Failed to rename task');
    }
  };

  const handleDeleteRun = async (run: any) => {
    const ok = window.confirm(`Delete "${run.name}" permanently? This cannot be undone.`);
    if (!ok) return;
    try {
      await apiService.deleteRun(run.id);
      showSuccess('Success', 'Task deleted successfully');
      await loadRuns();
    } catch (error) {
      showError('Error', error instanceof Error ? error.message : 'Failed to delete task');
    }
  };

  return (
    <div className="p-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-windows-200 dark:border-gray-700">
        <div className="p-4 border-b border-windows-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setActiveTab('owned')}
              className={`px-3 py-1.5 rounded-lg text-sm ${
                activeTab === 'owned'
                  ? 'bg-accent-600 text-white'
                  : 'bg-windows-100 dark:bg-gray-700 text-windows-700 dark:text-gray-300'
              }`}
            >
              Owned
            </button>
            <button
              onClick={() => setActiveTab('shared')}
              className={`px-3 py-1.5 rounded-lg text-sm ${
                activeTab === 'shared'
                  ? 'bg-accent-600 text-white'
                  : 'bg-windows-100 dark:bg-gray-700 text-windows-700 dark:text-gray-300'
              }`}
            >
              Shared
            </button>
          </div>
          <span className="text-xs text-windows-500 dark:text-gray-400">
            {filteredRuns.length} tasks
          </span>
        </div>

        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-windows-50 dark:bg-gray-900">
              <tr className="text-left text-windows-600 dark:text-gray-300">
                <th className="px-4 py-3">Task Name</th>
                <th className="px-4 py-3">Run ID</th>
                <th className="px-4 py-3">
                  {activeTab === 'shared' ? 'Expires' : 'Created'}
                </th>
                <th className="px-4 py-3">Owner</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {state.isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-windows-500 dark:text-gray-400">
                    <div className="inline-flex items-center gap-2">
                      <Loader2 size={16} className="animate-spin" />
                      <span>Loading tasks...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredRuns.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-windows-500 dark:text-gray-400">
                    No tasks found in this tab.
                  </td>
                </tr>
              ) : (
                filteredRuns.map((run: any) => (
                  <tr key={run.id} className="group border-t border-windows-200 dark:border-gray-700">
                    <td className="px-4 py-3 font-medium text-windows-900 dark:text-gray-100">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate">{run.name}</span>
                        {activeTab === 'owned' && (
                          <div className="inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              type="button"
                              onClick={() => handleRenameRun(run)}
                              className="p-1 rounded hover:bg-windows-100 dark:hover:bg-gray-700 text-windows-600 dark:text-gray-300"
                              title="Rename task"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteRun(run)}
                              className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600"
                              title="Delete task"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-windows-600 dark:text-gray-300">{run.id}</td>
                    <td className="px-4 py-3 text-windows-600 dark:text-gray-300">
                      {activeTab === 'shared'
                        ? formatIsoDateTime(run.shared_expires_at)
                        : formatDate(run.timestamp)}
                    </td>
                    <td className="px-4 py-3 text-windows-600 dark:text-gray-300">
                      {run.owner_display_name || run.owner_email || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {run.has_schema && <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">Schema</span>}
                        {run.has_synthetic_data && <span className="px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700">Generated</span>}
                        {run.shared_with_me && <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">Shared</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => navigate(`/run/${run.id}`)}
                          className="px-2.5 py-1.5 rounded border border-windows-300 dark:border-gray-600 text-windows-700 dark:text-gray-300 hover:bg-windows-100 dark:hover:bg-gray-700 inline-flex items-center gap-1"
                        >
                          <Eye size={14} />
                          <span>Open</span>
                        </button>
                        <button
                          onClick={() => handleDownloadAllCSV(run.id)}
                          disabled={isDownloading}
                          className="px-2.5 py-1.5 rounded border border-windows-300 dark:border-gray-600 text-windows-700 dark:text-gray-300 hover:bg-windows-100 dark:hover:bg-gray-700 inline-flex items-center gap-1"
                        >
                          <Download size={14} />
                          <span>CSV</span>
                        </button>
                        <button
                          onClick={() => handleDownloadAllXML(run.id)}
                          disabled={isDownloading}
                          className="px-2.5 py-1.5 rounded border border-windows-300 dark:border-gray-600 text-windows-700 dark:text-gray-300 hover:bg-windows-100 dark:hover:bg-gray-700 inline-flex items-center gap-1"
                        >
                          <Download size={14} />
                          <span>XML</span>
                        </button>
                        {activeTab === 'owned' && (
                          <button
                            onClick={() => setShareRunId(run.id)}
                            className="px-2.5 py-1.5 rounded border border-accent-300 text-accent-600 hover:bg-accent-50 inline-flex items-center gap-1"
                          >
                            <Share2 size={14} />
                            <span>Share</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {shareRunId && (() => {
        const run = state.runs.find((r) => r.id === shareRunId);
        return run ? (
          <ShareRunModal
            runId={shareRunId}
            runName={run.name}
            onClose={() => setShareRunId(null)}
            onShared={() => {}}
          />
        ) : null;
      })()}
    </div>
  );
};

export default TdmTaskLifecyclePage;

